import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Minus, Plus, ShoppingBag, Truck, Tag } from "lucide-react";

export default function Cart() {
  const { items, remove, setQty, subtotal, hasPhysical } = useCart();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: coupon.trim(), items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      setApplied(data.coupon); toast.success(`Coupon applied — you save ${inr(data.coupon.discount)}`);
    } catch (e) { setApplied(null); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  if (!items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-navy">Your cart is empty</h1>
        <p className="text-slate-500 mt-2">Explore our business systems and add your first product.</p>
        <Button asChild className="mt-6 bg-teal hover:bg-teal-dark text-white"><Link to="/shop">Browse products</Link></Button>
      </div>
    );
  }

  const discount = applied?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-navy mb-6">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {items.map((i) => (
            <div key={i.product_id} className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4" data-testid={`cart-item-${i.slug}`}>
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                {i.cover_image && <img src={i.cover_image} alt={i.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <Link to={`/product/${i.slug}`} className="font-medium text-navy hover:text-teal">{i.name}</Link>
                  <button onClick={() => remove(i.product_id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${i.slug}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <span className="text-xs text-slate-400">{i.requires_shipping ? "Physical" : "Digital"}</span>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-slate-200 rounded-lg">
                    <button className="p-2" onClick={() => setQty(i.product_id, i.quantity - 1)}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="px-3 text-sm font-medium" data-testid={`cart-qty-${i.slug}`}>{i.quantity}</span>
                    <button className="p-2" onClick={() => setQty(i.product_id, i.quantity + 1)}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="font-semibold text-navy">{inr(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-xl p-5 sticky top-20">
            <h2 className="font-heading font-semibold text-navy mb-4">Order Summary</h2>
            <div className="flex gap-2 mb-4">
              <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="cart-coupon-input" />
              <Button variant="outline" onClick={applyCoupon} disabled={checking} data-testid="cart-coupon-apply"><Tag className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium">{inr(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-teal"><span>Discount ({applied.code})</span><span>-{inr(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Shipping</span>
                <span className="font-medium">{hasPhysical ? "Calculated at checkout" : "Free (digital)"}</span></div>
              <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between text-base font-bold text-navy">
                <span>Total</span><span data-testid="cart-total">{inr(total)}</span></div>
            </div>
            {hasPhysical && <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><Truck className="w-3 h-3" /> Shipping address required at checkout</p>}
            <Button className="w-full mt-5 bg-teal hover:bg-teal-dark text-white" size="lg"
              onClick={() => navigate("/checkout", { state: { coupon: applied?.code } })} data-testid="cart-checkout-btn">
              Proceed to Checkout
            </Button>
            <Link to="/shop" className="block text-center text-sm text-slate-500 mt-3 hover:text-teal">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
